import React from 'react';
import JsonLd, { SITE, PROVIDER } from '../components/JsonLd';
import SEO from '../components/SEO';
import { Link } from 'react-router-dom';
import {
  Building2,
  ShieldCheck,
  Users,
  UserRound,
  Leaf,
  TrendingUp,
  Handshake,
  Award,
  CheckCircle2,
  Globe,
  HeartPulse,
  Star,
  Home,
  Network,
  HeartHandshake,
  Brain,
  HandHeart,
  Store,
  Landmark,
  Heart,
} from 'lucide-react';
import SubpageShell from './SubpageShell';
import '../styles/DevelopersPartners.css';
import '../styles/StrategicAlignment.css';

/* ---------- "Why partner with N3URO WORLD" cards ---------- */
const WHY_CARDS = [
  {
    icon: UserRound,
    accent: '#5BE9FF',
    title: 'Social\nImpact',
    body: 'Deliver measurable social value for neurodivergent families and the wider community.',
  },
  {
    icon: Leaf,
    accent: '#7CE38B',
    title: 'Sustainable\nDevelopment',
    body: 'Create inclusive, future-ready spaces that stand the test of time.',
  },
  {
    icon: TrendingUp,
    accent: '#FF6BC2',
    title: 'Economic\nValue',
    body: 'Drive footfall, community spend and long-term asset value.',
  },
  {
    icon: Handshake,
    accent: '#C77DFF',
    title: 'Strategic\nPartnerships',
    body: 'Work alongside a mission-led brand with a clear, proven infrastructure model.',
  },
  {
    icon: Award,
    accent: '#FFE066',
    title: 'Reputation\n& Leadership',
    body: 'Be recognised as a leader in inclusive innovation and social change.',
  },
];

/* ---------- "We collaborate with" checklist ---------- */
const COLLAB = [
  {
    title: 'DEVELOPERS',
    accent: 'AND LANDLORDS',
    body: 'Repurpose and reimagine spaces that create lasting impact.',
    dot: '#5BE9FF',
  },
  {
    title: 'INVESTORS',
    accent: 'AND FUNDERS',
    body: 'Support scalable infrastructure that changes lives.',
    dot: '#C77DFF',
  },
  {
    title: 'LOCAL AUTHORITIES',
    accent: 'AND HOUSING ASSOCIATIONS',
    body: 'Strengthen communities through inclusive infrastructure.',
    dot: '#FF6BC2',
  },
  {
    title: 'CORPORATES',
    accent: 'AND CSR PARTNERS',
    body: 'Align your brand with purpose-driven, measurable change.',
    dot: '#7CE38B',
  },
  {
    title: 'EDUCATION, HEALTH',
    accent: 'AND COMMUNITY PARTNERS',
    body: 'Build connected ecosystems of support and opportunity.',
    dot: '#FFE066',
  },
];

/* ============================================================
   STRATEGIC ALIGNMENT — merged from former /strategic-alignment page
   ============================================================ */

/* ---------- We Work Across ---------- */
const ALIGNMENT_AREAS = [
  {
    icon: Building2,
    color: '#5BE9FF',
    title: 'Developers & Built Environment',
    desc: 'Embedding neuro-inclusive family access into developments, places and environments.',
  },
  {
    icon: Network,
    color: '#FFE066',
    title: 'Transport & Mobility',
    desc: 'Supporting more accessible family journeys across transport environments and networks.',
  },
  {
    icon: Home,
    color: '#FF6BC2',
    title: 'Housing & Residential',
    desc: 'Strengthening neuro-inclusive family access across homes, developments and communities.',
  },
  {
    icon: Landmark,
    color: '#C77DFF',
    title: 'Local Authorities & Public Sector',
    desc: 'Supporting organisations responsible for the places, services and infrastructure families rely on.',
  },
  {
    icon: HeartPulse,
    color: '#5BE9FF',
    title: 'Healthcare',
    desc: 'Advancing neuro-inclusive family access across healthcare environments and services.',
  },
  {
    icon: Store,
    color: '#FF6BC2',
    title: 'Hospitality & Destinations',
    desc: 'Helping organisations create more accessible experiences for families across hospitality, leisure and visitor environments.',
  },
  {
    icon: Users,
    color: '#C77DFF',
    title: 'Commercial & Workplace',
    desc: 'Supporting neuro-inclusive approaches across commercial environments, workplaces and customer-facing spaces.',
  },
  {
    icon: HandHeart,
    color: '#FFE066',
    title: 'Public & Community Infrastructure',
    desc: 'Bringing neuro-inclusive family access into the everyday places and environments communities use.',
  },
];

/* ---------- Development Models ---------- */
const DEV_MODELS = [
  { icon: Star,    color: '#FFE066', title: 'Flagship Centres',           desc: 'Large-scale destination environments that become community anchors.' },
  { icon: Users,   color: '#5BE9FF', title: 'Integrated Community Hubs',  desc: 'Embedded within mixed-use developments and town centre schemes.' },
  { icon: Home,    color: '#FF6BC2', title: 'Residential Lounge Models',  desc: 'Smaller, targeted spaces focused on regulation, support and connection.' },
  { icon: Network, color: '#C77DFF', title: 'Future Network Expansion',   desc: 'Replicable model designed for multi-site growth and regional impact.' },
];

/* ---------- Social Value & ESG ---------- */
const SOCIAL_VALUE = [
  { icon: HeartHandshake, color: '#5BE9FF', label: 'Family engagement' },
  { icon: Brain,          color: '#C77DFF', label: 'Neurodivergent support' },
  { icon: HeartPulse,     color: '#FF6BC2', label: 'Community wellbeing' },
  { icon: TrendingUp,     color: '#FFE066', label: 'Regeneration outcomes' },
  { icon: HandHeart,      color: '#5BFF8A', label: 'Inclusive access' },
  { icon: ShieldCheck,    color: '#FFB5A0', label: 'Long-term community benefit' },
];

const nl = (text) =>
  text.split('\n').map((line, i) => (
    <React.Fragment key={`${line}-${i}`}>
      {i > 0 && <br />}
      {line}
    </React.Fragment>
  ));

const DevelopersPartners = () => {
  return (
    <SubpageShell currentPath="/developers-partners">
      <SEO
        title="Developers & Partners"
        description="Neuro-Inclusive Family Infrastructure for housing, regeneration, education and health developers. Delivery models, social value and long-term partnership with N3URO WORLD®."
        path="/developers-partners"
      />
      <JsonLd data={{ '@type': 'Service', name: 'Neuro-inclusive family infrastructure partnerships', url: `${SITE}/developers-partners`, description: 'Partnership route for developers, investors and organisations to build neuro-inclusive family access into environments and developments.', provider: PROVIDER, areaServed: 'GB' }} />
      {/* Native hero — building photo used as FULL-BLEED background,
          all copy rendered as HTML on top so it stays crisp on mobile.
          Matches the source mockup layout: gradient title + subtitle on
          the left, 5 principle cards on the right, 5-icon bottom strip,
          and an EXPLORE · REGULATE · CONNECT · n3uro.world footer strip. */}
      <section className="dp-hero-poster" data-testid="dp-hero" style={{ '--dp-hero-bg': "url('/img/dp-building.webp')" }}>
        <div className="dp-hero-bg" aria-hidden="true" />
        <div className="dp-hero-inner">
          <div className="dp-hero-copy">
            <h1 className="dp-hero-title">
              <span className="dp-hero-title-white">DEVELOPERS</span>
              <span className="dp-hero-title-grad">&amp; PARTNERS</span>
            </h1>
            <div className="dp-hero-rule" aria-hidden="true" />
            <p className="dp-hero-subtitle">
              <span className="dp-hero-sub-white">BUILDING TOMORROW.</span>{' '}
              <span className="dp-hero-sub-gold">TOGETHER.</span>
            </p>
            <p className="dp-hero-lede">
              <span className="dp-hero-brand">N3URO WORLD®</span> partners with developers, investors and
              organisations to design, build and operate neuro-inclusive family infrastructure: more than
              amenity, a scalable model that strengthens placemaking and long-term social value.
            </p>
            <div className="conv-cta-row" data-testid="dp-hero-ctas">
              <Link to="/infrastructure-enquiry" className="conv-btn-primary" data-cta="dp-hero-partnership-enquiry" data-testid="dp-cta-partnership">
                Start a partnership enquiry
              </Link>
              <Link to="/advisory" className="conv-btn-ghost" data-cta="dp-hero-advisory" data-testid="dp-cta-advisory">
                Explore N3URO Advisory
              </Link>
            </div>
          </div>

          <ul className="dp-hero-principles" aria-label="Why partner with N3URO WORLD®" data-testid="dp-why">
            {WHY_CARDS.map(({ icon: Icon, accent, title, body }) => (
              <li key={title} className="dp-hero-principle" style={{ '--accent': accent }} data-testid={`dp-why-${title.split('\n')[0].toLowerCase().replace(/\s+/g, '-')}`}>
                <span className="dp-hero-principle-icon" aria-hidden="true"><Icon size={22} strokeWidth={1.8} /></span>
                <div>
                  <h2 className="dp-hero-principle-title">{nl(title.toUpperCase())}</h2>
                  <p className="dp-hero-principle-body">{body}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>

        <div className="dp-hero-footer">
          <p className="dp-hero-tagline" aria-hidden="true">
            <Heart size={14} strokeWidth={2} className="dp-hero-heart" aria-hidden="true" />
            <span className="dp-hero-tag-cyan">EXPLORE.</span>
            <span className="dp-hero-tag-sep">|</span>
            <span className="dp-hero-tag-mag">REGULATE.</span>
            <span className="dp-hero-tag-sep">|</span>
            <span className="dp-hero-tag-gold">CONNECT.</span>
          </p>
          <a
            href="https://n3uro.world"
            className="dp-hero-link"
            data-testid="dp-hero-site-link"
          >
            <Globe size={14} strokeWidth={2} aria-hidden="true" />
            <span>WWW.N3URO.WORLD</span>
          </a>
        </div>
      </section>

      <main className="dp-page" data-testid="developers-partners-page">
        {/* ================= COLLABORATE WITH ================= */}
        <section className="dp-collab" data-testid="dp-collaborate">
          <figure className="dp-collab-illus" aria-hidden="true">
            <img width="445" height="278" decoding="async"
              src="/img/dp-collab.webp"
              alt=""
              loading="lazy"
            />
          </figure>

          <div className="dp-collab-right">
            <h2 className="dp-section-title dp-section-title--left">
              <span className="dp-section-title-a">We collaborate with</span>
            </h2>

            <ul className="dp-collab-list" role="list">
              {COLLAB.map(({ title, accent, body, dot }) => (
                <li
                  key={title}
                  className="dp-collab-item"
                  style={{ '--dot': dot }}
                  data-testid={`dp-collab-${title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                  tabIndex={0}
                >
                  <span className="dp-collab-check" aria-hidden="true">
                    <CheckCircle2 size={22} strokeWidth={2.2} />
                  </span>
                  <div className="dp-collab-copy">
                    <p className="dp-collab-h">
                      <strong>{title}</strong>{' '}
                      <span className="dp-collab-h-accent">{accent}</span>
                    </p>
                    <p className="dp-collab-b">{body}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section className="sa-block" data-testid="sa-alignment-areas">
          <h2 className="sa-block-eyebrow">We Work Across</h2>
          <div className="sa-areas-grid">
            {ALIGNMENT_AREAS.map(({ icon: Icon, color, title, desc }) => (
              <article key={title} className="sa-area-card" style={{ '--accent': color }}>
                <header className="sa-area-head">
                  <span className="sa-area-icon"><Icon size={22} strokeWidth={2} /></span>
                  <h3 className="sa-area-title">{title}</h3>
                </header>
                <p className="sa-area-desc">{desc}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="sa-block" data-testid="sa-dev-models">
          <h2 className="sa-block-eyebrow">Development Models</h2>
          <div className="sa-models-grid">
            {DEV_MODELS.map(({ icon: Icon, color, title, desc }) => (
              <article key={title} className="sa-model-card" style={{ '--accent': color }}>
                <span className="sa-model-icon"><Icon size={26} strokeWidth={2} /></span>
                <h3 className="sa-model-title">{title}</h3>
                <p className="sa-model-desc">{desc}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="sa-block" data-testid="sa-value-partners">
          <h2 className="sa-block-eyebrow">Social Value &amp; ESG: Measurable Impact</h2>
          <ul className="sa-value-grid">
            {SOCIAL_VALUE.map(({ icon: Icon, color, label }) => (
              <li key={label} className="sa-value-item" style={{ '--icon-accent': color }}>
                <span className="sa-value-icon"><Icon size={20} strokeWidth={1.8} /></span>
                <span>{label}</span>
              </li>
            ))}
          </ul>
        </section>

        <section className="sa-final dp-final" id="dp-enquire" data-testid="dp-final-cta">
          <div className="sa-final-text">
            <h2 className="sa-final-title">
              <span className="sa-hl sa-hl-cyan">Let&rsquo;s build infrastructure</span>
              <br />
              <span className="sa-hl sa-hl-mag">families actually need.</span>
            </h2>
            <p>
              Planning a site, development or portfolio? Tell us about it and we&rsquo;ll come back with how
              N3URO WORLD<sup>®</sup> could fit.
            </p>
          </div>
          <div className="conv-cta-row dp-final-ctas">
            <Link to="/infrastructure-enquiry" className="conv-btn-primary" data-cta="dp-final-partnership-enquiry" data-testid="dp-cta-conversation">
              Start a partnership enquiry
            </Link>
            <a href="mailto:hello@n3uro.world" className="conv-btn-ghost" data-testid="dp-cta-email">hello@n3uro.world</a>
          </div>
        </section>

      </main>
    </SubpageShell>
  );
};

export default DevelopersPartners;
